import { Link } from "react-router-dom";
import { Field, inputCls } from "../../pages/Contact";
import { FileDrop } from "./FileDrop";
import { LANGUAGE_OPTIONS } from "../../lib/routes";
import { ShieldCheck } from "lucide-react";

const Title = ({ children }) => <h2 className="font-display text-2xl font-light text-[var(--ink)] md:text-3xl">{children}</h2>;
const Radio = ({ name, value, checked, onChange, label, sub, id }) => (
  <label className={`flex cursor-pointer items-start gap-4 rounded-sm border p-5 transition-[border-color,background-color] duration-300 ${checked ? "border-[var(--ink)] bg-white shadow-[0_10px_30px_-18px_rgba(18,32,43,0.4)]" : "border-[var(--line)] bg-white/60 hover:border-[var(--ink)]/40"}`} data-testid={id}>
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange} className="mt-1 h-4 w-4 accent-[var(--teal-ink)]" />
    <span><span className="block font-medium text-[var(--ink)]">{label}</span>{sub && <span className="mt-0.5 block text-sm text-[var(--muted)]">{sub}</span>}</span>
  </label>
);

export const StepService = ({ u, f, set, errors }) => (
  <div className="space-y-6">
    <Title>{u.s1.title}</Title>
    <div className="grid gap-3 md:grid-cols-2">
      {Object.entries(u.s1.opts).map(([k, [l, s]]) => <Radio key={k} name="service" value={k} checked={f.service === k} onChange={() => set("service", k)} label={l} sub={s} id={`service-opt-${k}`} />)}
    </div>
    {errors.service && <p className="text-sm text-[#B42318]" role="alert">{errors.service}</p>}
  </div>
);

export const StepProject = ({ u, f, set, errors, lang }) => {
  const langs = LANGUAGE_OPTIONS[lang];
  const sel = (k, label) => (
    <Field label={label} error={errors[k]} id={`q-${k}`}>
      <select id={`q-${k}`} className={inputCls} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`q-${k}-select`}>
        <option value="">{u.s2.select}</option>
        {langs.map((l) => <option key={l} value={l}>{l}</option>)}
      </select>
    </Field>
  );
  return (
    <div className="space-y-6">
      <Title>{u.s2.title}</Title>
      <div className="grid gap-6 md:grid-cols-2">{sel("source_lang", u.s2.source)}{sel("target_lang", u.s2.target)}</div>
      <p className="-mt-3 text-xs text-[var(--muted)]">{u.s2.otherLang}</p>
      <Field label={u.s2.docType} error={errors.document_type} id="q-doc"><textarea id="q-doc" rows={3} className={inputCls} placeholder={u.s2.docTypePh} value={f.document_type} onChange={(e) => set("document_type", e.target.value)} data-testid="q-doc-input" /></Field>
      <Field label={u.s2.use} id="q-use"><input id="q-use" className={inputCls} placeholder={u.s2.usePh} value={f.intended_use} onChange={(e) => set("intended_use", e.target.value)} data-testid="q-use-input" /></Field>
      <fieldset>
        <legend className="mb-3 text-sm font-medium text-[var(--ink)]">{u.s2.authority}</legend>
        <div className="flex flex-wrap gap-2">
          {["yes", "no", "unsure"].map((k) => (
            <button type="button" key={k} onClick={() => set("for_authority", k)} className={`rounded-full border px-5 py-2.5 text-sm transition-colors ${f.for_authority === k ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--line)] bg-white text-[var(--ink)]"}`} data-testid={`q-authority-${k}`}>{u.s2[k]}</button>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-6 md:grid-cols-2">
        <Field label={u.s2.deadline} id="q-deadline"><input id="q-deadline" type="date" min={new Date().toISOString().slice(0, 10)} className={inputCls} value={f.deadline} onChange={(e) => set("deadline", e.target.value)} data-testid="q-deadline-input" /></Field>
        <Field label={u.s2.delivery} id="q-delivery">
          <select id="q-delivery" className={inputCls} value={f.delivery} onChange={(e) => set("delivery", e.target.value)} data-testid="q-delivery-select">
            <option value="">{u.s2.select}</option>{u.s2.deliveryOpts.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <Field label={u.s2.instructions} id="q-instr"><textarea id="q-instr" rows={3} className={inputCls} value={f.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="q-instructions-input" /></Field>
    </div>
  );
};

export const StepFiles = ({ u, f, cfg, up, errors }) => (
  <div className="space-y-6">
    <Title>{u.s3.title}</Title>
    <p className="rounded-sm border-l-2 border-[var(--teal)] bg-white px-5 py-4 text-sm text-[var(--ink)]">{u.s3.legible}</p>
    <FileDrop s={u.s3} cfg={cfg} up={up} />
    {f.service === "interpreting" && <p className="text-sm text-[var(--muted)]">{u.s3.optional}</p>}
    {errors.files && <p className="text-sm text-[#B42318]" role="alert" data-testid="files-required-error">{errors.files}</p>}
  </div>
);

export const StepContact = ({ u, f, set, errors }) => (
  <div className="space-y-6">
    <Title>{u.s4.title}</Title>
    <div className="grid gap-6 md:grid-cols-2">
      <Field label={u.s4.name} error={errors.name} id="q-name"><input id="q-name" className={inputCls} autoComplete="name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="q-name-input" /></Field>
      <Field label={u.s4.email} error={errors.email} id="q-email"><input id="q-email" type="email" className={inputCls} autoComplete="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="q-email-input" /></Field>
      <Field label={u.s4.phone} id="q-phone"><input id="q-phone" className={inputCls} autoComplete="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="q-phone-input" /></Field>
      <Field label={u.s4.country} id="q-country"><input id="q-country" className={inputCls} autoComplete="country-name" value={f.country} onChange={(e) => set("country", e.target.value)} data-testid="q-country-input" /></Field>
      <Field label={u.s4.company} id="q-company"><input id="q-company" className={inputCls} autoComplete="organization" value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="q-company-input" /></Field>
      <Field label={u.s4.pref} id="q-pref">
        <select id="q-pref" className={inputCls} value={f.contact_pref} onChange={(e) => set("contact_pref", e.target.value)} data-testid="q-pref-select">
          <option value="">{u.s2.select}</option>{u.s4.prefOpts.map((o) => <option key={o}>{o}</option>)}
        </select>
      </Field>
    </div>
  </div>
);

export const StepSubmit = ({ u, f, set, errors, up, cfg, privacyPath }) => {
  const done = up.files.filter((x) => x.status === "done");
  const rows = [[u.steps[0], u.s1.opts[f.service]?.[0]], [`${u.s2.source} → ${u.s2.target}`, `${f.source_lang} → ${f.target_lang}`], [u.s2.docType, f.document_type], [u.steps[2], `${done.length} ${u.s5.files}`], [u.s4.name, `${f.name} · ${f.email}`]];
  return (
    <div className="space-y-8">
      <Title>{u.s5.title}</Title>
      <dl className="divide-y divide-[var(--line)] rounded-sm border border-[var(--line)] bg-white" data-testid="quote-summary">
        {rows.map(([k, v]) => <div key={k} className="grid gap-1 px-5 py-4 sm:grid-cols-3"><dt className="text-sm text-[var(--muted)]">{k}</dt><dd className="text-sm text-[var(--ink)] sm:col-span-2 break-words">{v || "—"}</dd></div>)}
      </dl>
      <div className="rounded-sm bg-[var(--ink)] p-6 text-white md:p-8">
        <p className="flex items-center gap-3 font-display text-xl"><ShieldCheck className="h-5 w-5 text-[var(--teal)]" />{u.s5.privacyTitle}</p>
        <p className="mt-3 text-sm leading-relaxed text-white/80">{u.s5.privacy.replace("{d}", cfg.retention_days)}</p>
        <Link to={privacyPath} target="_blank" className="mt-3 inline-block text-sm text-[#9FD3CD] underline underline-offset-2" data-testid="quote-privacy-link">{u.s5.link}</Link>
      </div>
      <label className="flex items-start gap-3 text-sm text-[var(--ink)]">
        <input type="checkbox" checked={f.privacy_ack} onChange={(e) => set("privacy_ack", e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--teal-ink)]" data-testid="quote-privacy-checkbox" />
        <span>{u.s5.ack}</span>
      </label>
      {errors.privacy && <p className="text-sm text-[#B42318]" role="alert" data-testid="quote-privacy-error">{errors.privacy}</p>}
      <p className="text-sm text-[var(--muted)]">{u.notice}</p>
    </div>
  );
};
